import { Component, inject } from '@angular/core';
import { Event, EventType, IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';

import { NavigationConstants } from './common/constants/navigation.constants';
import { applicationInfoView, availableVersionView, UpgradeInfo } from './model/views';
import { filterNotNil } from './common/utils/utils';
import { ApplicationInfo } from './model/shared/application-info.model';
import { AppState } from './model/reducer';
import { DisplayComponentHelperService } from './common/services/display-component-helper.service';

const nonStrictMatchOptions: IsActiveMatchOptions = {
  paths: 'subset',
  queryParams: 'subset',
  fragment: 'ignored',
  matrixParams: 'ignored'
};

@Component({
    selector: 'adm4-root-layout',
    template: `
    <div class='full-height-flex'>
      @if (shouldShowTopNavBarOnCurrentPage | async) {
        <adm4-navigation></adm4-navigation>
      }
      <div class='remaining-space-flex-content-wrapper'>
        <div class="app-content remaining-space-flex-content">
          @if (isApplicationInfoLoaded | async) {
            <adm4-key-shortcuts-component></adm4-key-shortcuts-component>
          }
          <router-outlet></router-outlet>
          <router-outlet name='modal'></router-outlet>
        </div>
      </div>
      @if (shouldShowTopNavBarOnCurrentPage | async) {
        <adm4-footer [showVersion]='shouldShowTopNavBarOnCurrentPage | async'
          [upgradeAvailable]='upgradeAvailable | async'
        ></adm4-footer>
      }
      @if (isIdentityPlatform|async) {
        <mat-menu> <!-- this is a workaround for issue IP-732  May future me forgive this glorious hack!-->
        </mat-menu>
      }
    </div>`,
    standalone: false
})
export class AppLayoutComponent {
  private router = inject(Router);
  private store$ = inject<Store<AppState>>(Store);
  private readonly displayComponentHelperService = inject(DisplayComponentHelperService);

  public readonly upgradeAvailable: Observable<boolean>;
  public readonly isApplicationInfoLoaded: Observable<boolean>;
  public readonly shouldShowTopNavBarOnCurrentPage: Observable<boolean>;
  public isIdentityPlatform: Observable<boolean>;

  constructor() {
    const displayComponentHelperService = this.displayComponentHelperService;

    this.isIdentityPlatform = displayComponentHelperService.isIdentityPlatform;
    this.isApplicationInfoLoaded = this.store$.select(applicationInfoView).pipe(
        filterNotNil(),
        map((appInfo: ApplicationInfo) => appInfo !== null),
    );
    this.upgradeAvailable = this.store$.select(availableVersionView).pipe(
        map((availableVersion: undefined | false | UpgradeInfo): boolean => !!availableVersion),
    );
    this.shouldShowTopNavBarOnCurrentPage = combineLatest([
      this.router.events.pipe(
        filter((event: Event): event is NavigationEnd => EventType.NavigationEnd === (event as any).type),
        map(() => {
          const isLoginPage = this.router.isActive(NavigationConstants.LOGIN, nonStrictMatchOptions);
          const isGraphView = this.router.isActive(NavigationConstants.GRAPH_VIEW, nonStrictMatchOptions);
          return isLoginPage || isGraphView;
        }),
        startWith(false)
      ),
      this.displayComponentHelperService.isIdentityPlatform.pipe(startWith(false))
    ]).pipe(
      map(([isExcludedRoute, isIdentityPlatform]) => !(isExcludedRoute || isIdentityPlatform)),
      shareReplay()
    );
  }
}
